// AccountDropdown: Netflix-style account management dropdown
// Provides clean, organized access to user account features

import React, { useState, useEffect, useRef } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useSubscription } from '@/hooks/useSubscription'
import { useCredits } from '@/hooks/useCredits'
import {
  SubscriptionManagementModal,
  AccountSettingsModal,
  SecuritySettingsModal,
  HelpSupportModal
} from './account'
import {
  User,
  CreditCard,
  Settings,
  LogOut,
  ChevronDown,
  Crown,
  Activity,
  Shield,
  Bell,
  HelpCircle
} from 'lucide-react'

interface AccountDropdownProps {
  className?: string
  onNavigateToAccount?: (page: 'subscription' | 'settings' | 'security' | 'help') => void
}

export function AccountDropdown({ className = '', onNavigateToAccount }: AccountDropdownProps) {
  const { user, signOut } = useAuth()
  const { subscription } = useSubscription()
  const { balance } = useCredits()
  const [isOpen, setIsOpen] = useState(false)
  const [activeModal, setActiveModal] = useState<'subscription' | 'settings' | 'security' | 'help' | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Close dropdown on escape key
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false)
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const handleSignOut = async () => {
    setIsOpen(false)
    await signOut()
  }

  const openModal = (modal: 'subscription' | 'settings' | 'security' | 'help') => {
    if (onNavigateToAccount) {
      // Navigate to pages instead of opening modals
      onNavigateToAccount(modal)
      setIsOpen(false)
    } else {
      // Fallback to modal behavior
      setActiveModal(modal)
      setIsOpen(false)
    }
  }

  const closeModal = () => {
    setActiveModal(null)
  }

  const getUserInitials = () => {
    if (!user?.user_metadata?.full_name && !user?.email) return 'U'
    const name = user.user_metadata?.full_name || user.email
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const getUserDisplayName = () => {
    return user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User'
  }

  const getPlanDisplayName = () => {
    if (subscription?.plan_name) {
      return subscription.plan_name
    }
    return subscription?.status === 'active' ? 'Professional' : 'Free Trial'
  }

  const getCreditsDisplay = () => {
    if (balance?.balance !== undefined) {
      return balance.balance.toLocaleString()
    }
    return '...'
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Trigger Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 text-gray-700 hover:text-gray-900 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 rounded-md px-3 py-2"
        aria-label="Account menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {/* User Avatar */}
        {user?.user_metadata?.avatar_url ? (
          <img
            src={user.user_metadata.avatar_url}
            alt="Profile"
            className="w-8 h-8 rounded-full object-cover"
          />
        ) : (
          <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
            {getUserInitials()}
          </div>
        )}

        {/* User Name - Hidden on mobile */}
        <span className="hidden md:block text-sm font-medium">
          {getUserDisplayName()}
        </span>

        {/* Dropdown Arrow */}
        <ChevronDown
          className={`w-4 h-4 transition-transform duration-200 ${
            isOpen ? 'transform rotate-180' : ''
          }`}
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50 animate-in slide-in-from-top-2 duration-200">

          {/* Account Overview Header */}
          <div className="px-4 py-3 border-b border-gray-100">
            <div className="flex items-center space-x-3 mb-3">
              {user?.user_metadata?.avatar_url ? (
                <img
                  src={user.user_metadata.avatar_url}
                  alt="Profile"
                  className="w-10 h-10 rounded-full object-cover"
                />
              ) : (
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-medium">
                  {getUserInitials()}
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">{getUserDisplayName()}</p>
                <p className="text-sm text-gray-600">{user?.email}</p>
              </div>
            </div>

            {/* Quick Status */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="flex items-center space-x-2">
                <Crown className="w-4 h-4 text-blue-600" />
                <span className="text-gray-700">{getPlanDisplayName()}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Activity className="w-4 h-4 text-green-600" />
                <span className="text-gray-700">{getCreditsDisplay()} credits</span>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">

            {/* Subscription Management */}
            <button
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 group"
              onClick={() => openModal('subscription')}
            >
              <CreditCard className="w-5 h-5 text-gray-500 group-hover:text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Manage Subscription</p>
                <p className="text-xs text-gray-600">Billing, plans, and payment methods</p>
              </div>
            </button>

            {/* Account Settings */}
            <button
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 group"
              onClick={() => openModal('settings')}
            >
              <Settings className="w-5 h-5 text-gray-500 group-hover:text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Account Settings</p>
                <p className="text-xs text-gray-600">Profile, security, and preferences</p>
              </div>
            </button>

            {/* Security */}
            <button
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 group"
              onClick={() => openModal('security')}
            >
              <Shield className="w-5 h-5 text-gray-500 group-hover:text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Security</p>
                <p className="text-xs text-gray-600">Password and account security</p>
              </div>
            </button>

            {/* Help & Support */}
            <button
              className="w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 flex items-center space-x-3 group"
              onClick={() => openModal('help')}
            >
              <HelpCircle className="w-5 h-5 text-gray-500 group-hover:text-blue-600" />
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-900">Help & Support</p>
                <p className="text-xs text-gray-600">Get help and contact support</p>
              </div>
            </button>

          </div>

          {/* Divider */}
          <div className="border-t border-gray-100 my-1"></div>

          {/* Sign Out */}
          <button
            onClick={handleSignOut}
            className="w-full px-4 py-3 text-left hover:bg-red-50 transition-colors duration-150 flex items-center space-x-3 group"
          >
            <LogOut className="w-5 h-5 text-gray-500 group-hover:text-red-600" />
            <span className="text-sm font-medium text-gray-900 group-hover:text-red-600">
              Sign Out
            </span>
          </button>

        </div>
      )}

      {/* Modals */}
      <SubscriptionManagementModal
        isOpen={activeModal === 'subscription'}
        onClose={closeModal}
        onUpgrade={() => {
          closeModal()
          // Could navigate to pricing page here if needed
        }}
      />

      <AccountSettingsModal
        isOpen={activeModal === 'settings'}
        onClose={closeModal}
      />

      <SecuritySettingsModal
        isOpen={activeModal === 'security'}
        onClose={closeModal}
      />

      <HelpSupportModal
        isOpen={activeModal === 'help'}
        onClose={closeModal}
      />
    </div>
  )
}

export default AccountDropdown